/* eslint-disable @nx/enforce-module-boundaries */
import { Box, useToast } from '@chakra-ui/react';
import { Switch } from '@gamma/ui/form-fields';
import { GraphQLReqStatus } from '@gamma/ui/progress';
import { Dispatch, SetStateAction, useContext } from 'react';
import { AlertMetadata } from '../../../../../../shared/generated/graphql';
import { i18n } from '../../../../../../shared/localization';
import { AuthContext } from '../../../../../../shared/providers/AuthProvider';
import {
  DETECTION_METHOD_SUMMARY_QUERY,
  QUERY_ALERT_METADATA_PAGINATED,
  QueryAlertMetadataPaginatedVariables,
  useSetAlertActiveStatus,
} from '../../../../queries';

const {
  active: activeText,
  alertCategoryStatus,
  errorRes,
  inactive,
  updateStatus,
  successRes,
  updateUnsuccessful,
} = i18n.pages.system.alertCatalog;

export interface AlertMetaStatusSwitchProps {
  tenant: string;
  activeRow?: Partial<AlertMetadata>;
  variables: QueryAlertMetadataPaginatedVariables;
  setActiveRow?: Dispatch<SetStateAction<Partial<AlertMetadata>>>;
}

export const AlertMetaStatusSwitch = ({
  tenant,
  activeRow,
  setActiveRow,
  variables,
}: AlertMetaStatusSwitchProps) => {
  const { active, title, logsource, content_id } = activeRow || {};

  const showToastMessage = useToast();

  const { userRole } = useContext(AuthContext);

  const [setAlertActiveStatus, { loading }] = useSetAlertActiveStatus({
    awaitRefetchQueries: true,
    notifyOnNetworkStatusChange: true,
    refetchQueries: [
      {
        query: DETECTION_METHOD_SUMMARY_QUERY,
        variables: {
          tenant,
        },
      },
      {
        query: QUERY_ALERT_METADATA_PAGINATED,
        variables,
      },
    ],
    onError: () => {
      showToastMessage({
        status: 'error',
        title: errorRes,
        description: updateUnsuccessful,
        isClosable: true,
        position: 'bottom-right',
      });
    },
    onCompleted: ({ setAlertActiveStatus }) => {
      setActiveRow?.({
        ...activeRow,
        active: Boolean(setAlertActiveStatus?.metadata?.[0]?.active),
      });
      showToastMessage({
        status: 'success',
        title: successRes,
        description: i18n.formatString(
          updateStatus,
          active ? inactive : activeText,
        ),
        isClosable: true,
        position: 'bottom-right',
      });
    },
  });

  if (loading) {
    return (
      <Box
        display="inline-flex"
        width="24px"
        height="24px"
        alignItems="center"
        justifyContent="center"
        overflow="hidden"
      >
        <GraphQLReqStatus spinnerSize="sm" loading={loading} />
      </Box>
    );
  }

  return (
    <Switch
      name="status"
      isChecked={Boolean(active)}
      isLabelHidden={true}
      label={alertCategoryStatus}
      isDisabled={userRole === 'viewer'}
      data-testid="alert-meta-status-switch"
      onChange={() =>
        setAlertActiveStatus({
          variables: {
            items: [
              {
                tenant,
                content_id,
                active: !active,
                alert_name: title,
                alert_type: logsource?.category,
              },
            ],
          },
        })
      }
    />
  );
};
